import { CircularProgress, Link } from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { isEmpty } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import {
  getTargetingPresetSummary,
  ITargetingPreset,
  ITargetingPresetVars,
} from '~/src/dash/apiV2/targeting/targetingPresets';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { IndependentSelect } from '~/src/dash/components/dropdowns/IndependentSelect';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import {
  borderRadius,
  Col,
  Cols,
  Div,
  Panel,
  Row,
  Rows,
  spacing,
  Text,
} from '~/src/dash/publicaUI';
import { extractRouteParams, getRoutePath, RouteTargetingList } from '~/src/dash/router/routes';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  targetingPresetDropdowns,
  TargetingPresetParameters,
  TargetingPresetParamKey,
} from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/config';
import { TargetingSummaryTable } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/TargetingSummaryTable';
import {
  ITargetingSummaryTableColumn,
  ITargetingSummaryTableRow,
} from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/TargetingSummaryTable/config';

const iconColWidth = 32;

interface ICaretUpDownProps {
  isExpanded: boolean;
}

const CaretUpDown = ({ isExpanded }: ICaretUpDownProps) => {
  if (isExpanded) {
    return <KeyboardArrowUpIcon fontSize='small' />;
  } else {
    return <KeyboardArrowDownIcon fontSize='small' />;
  }
};

const generateColumns = (targeting: ITargetingPreset[]) => {
  const columns: ITargetingSummaryTableColumn[] = [
    {
      key: 'Parameter',
      label: 'Targeting Field',
    },
  ];
  targeting.forEach(({ ID, Name }) => {
    columns.push({
      key: String(ID),
      label: Name,
    });
  });
  return columns;
};

const generateRows = (
  activeParameterKeys: TargetingPresetParamKey[],
  publisherID: string,
  isSuperUser: boolean,
) => {
  const rows: ITargetingSummaryTableRow[] = [];
  if (!activeParameterKeys) {
    return [];
  }
  activeParameterKeys.forEach((paramKey) => {
    const param = TargetingPresetParameters[paramKey];
    if (param.checkIfHidden && param.checkIfHidden(publisherID, isSuperUser)) return;
    rows.push({
      key: param.key,
      label: param.label,
    });
  });
  return rows;
};

interface IProps {
  generateCustomTargeting: () => ITargetingPresetVars;
  presetIDs: number[];
  handleApplySelectedPresets: (presetIDs: Set<number>) => void;
  presetOptions: ISelectOption[];
  conflicts: Record<string, TargetingPresetParamKey[]>;
  conflictedParamKeys: TargetingPresetParamKey[];
}

export const TargetingIntegration = ({
  generateCustomTargeting,
  presetIDs = [],
  presetOptions = [],
  handleApplySelectedPresets,
  conflicts,
  conflictedParamKeys,
}: IProps) => {
  const { session, user, isSuperUser } = useBaseStore();

  const isMounted = useRef(false);

  const [listsLoadState, setListsLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [lists, setLists] = useState<Record<string, ISelectOption[]>>({});
  const [loadState, setLoadState] = useState(LoadingStateLoading);

  const [isActiveFieldsOpen, setIsActiveFieldsOpen] = useState(false);
  const [isPresetsOpen, setIsPresetsOpen] = useState(!isEmpty(presetIDs));

  const [columns, setColumns] = useState<ITargetingSummaryTableColumn[]>([]);
  const [rows, setRows] = useState<ITargetingSummaryTableRow[]>([]);
  const [data, setData] = useState<ITargetingPreset[]>([]);

  const [conflictedKeys, setConflictedKeys] = useState(conflictedParamKeys);

  useEffect(() => {
    populateDropdowns();
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      setConflictedKeys(conflictedParamKeys);
    }
  }, [conflictedParamKeys]);

  useEffect(() => {
    if (isMounted.current) {
      handleGenerateSummary();
    } else {
      isMounted.current = true;
    }
  }, [lists, presetIDs, conflictedParamKeys]);

  const populateDropdowns = () => {
    setListsLoadState(LoadingStateLoading);
    const keys: string[] = [];
    const getListFns: Array<Promise<ISelectOption[]>> = [];
    Object.values(targetingPresetDropdowns).forEach((dropdown) => {
      keys.push(dropdown.key);
      getListFns.push(dropdown.getList(session, user));
    });
    const tempLists: Record<string, ISelectOption[]> = {};
    Promise.all(getListFns)
      .then((results) => {
        results.forEach((result, i) => {
          tempLists[keys[i]] = result;
        });
        unstable_batchedUpdates(() => {
          setLists(tempLists);
          setListsLoadState(!isEmpty(tempLists) ? LoadingStateHasData : LoadingStateNoData);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setLists({});
          setListsLoadState(LoadingStateNoData);
        });
      });
  };

  const handleGenerateSummary = () => {
    setLoadState(LoadingStateLoading);
    const customTargeting = generateCustomTargeting();
    getTargetingPresetSummary(session.publisher.ID, {
      customTargeting,
      presetIDs,
    })
      .then(({ Targeting, ActiveParameters, ConflictedParameters }) => {
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateHasData);
          setColumns(generateColumns(Targeting));
          setRows(generateRows(ActiveParameters, session.publisher.ID, isSuperUser));
          setData(Targeting);
          setConflictedKeys(ConflictedParameters);
          if (ActiveParameters) {
            setIsActiveFieldsOpen(true);
          }
        });
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const applyPresets = (presetIDs: Set<number>) => {
    unstable_batchedUpdates(() => {
      handleApplySelectedPresets(presetIDs);
      setIsActiveFieldsOpen(true);
      setConflictedKeys([]);
    });
  };

  const isSummaryReady =
    loadState !== LoadingStateLoading && listsLoadState === LoadingStateHasData && !isEmpty(lists);

  return (
    <Panel enableSize>
      <Rows gap={spacing.base}>
        <Row
          smallVerticalPadding
          style={{ borderRadius: borderRadius.small, backgroundColor: publicaColors.veryLightGray }}
        >
          <Cols
            gap={spacing.large}
            style={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Col flex>
              <Text small>
                Use refresh to see your latest edits and review active fields before saving changes.
              </Text>
            </Col>
            <Col>
              <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
                <Col>
                  <Div
                    smallHorizontalPadding
                    onClick={() => {
                      handleGenerateSummary();
                      unstable_batchedUpdates(() => {
                        setIsActiveFieldsOpen(true);
                        setConflictedKeys([]);
                      });
                    }}
                    style={{
                      border: `1px solid ${publicaColors.lightBlue}`,
                      borderRadius: borderRadius.small,
                      backgroundColor: publicaColors.white,
                    }}
                  >
                    <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                      <Col displayFlex>
                        <IconV2 name='sync' small color={publicaColors.blue} />
                      </Col>
                      <Col>
                        <Text small bold color={publicaColors.blue}>
                          Refresh Changes
                        </Text>
                      </Col>
                    </Cols>
                  </Div>
                </Col>
              </Cols>
            </Col>
          </Cols>
        </Row>
        {/* ERRORS */}
        {!isEmpty(conflicts) && (
          <Row noPadding>
            <Text small color={publicaColors.red}>
              Please Resolve All Conflicts
            </Text>
          </Row>
        )}
        {/* ACTIVE FIELDS */}
        <Row noPadding>
          <Rows>
            <Row noPadding>
              <Cols
                gap={spacing.small}
                style={{ alignItems: 'center', cursor: 'pointer' }}
                onClick={() => setIsActiveFieldsOpen(!isActiveFieldsOpen)}
              >
                <Col width={iconColWidth} displayFlex>
                  <IconV2 name='radio' small />
                </Col>
                <Col>
                  <Text small bold>
                    Active Fields
                  </Text>
                </Col>
                {loadState === LoadingStateLoading ? (
                  <Col displayFlex>
                    <CircularProgress size={12} />
                  </Col>
                ) : (
                  <Col>
                    <Text small bold>
                      {`(${rows.length})`}
                    </Text>
                  </Col>
                )}
                <Col displayFlex>
                  <CaretUpDown isExpanded={isActiveFieldsOpen} />
                </Col>
              </Cols>
            </Row>
            {isActiveFieldsOpen && (
              <Row noPadding>
                <Rows paddingLeft={`${iconColWidth + 8}px`}>
                  <Row noPadding>
                    <Text small>
                      A compiled view of all targeting fields set using Presets and Custom Targeting
                      below.
                    </Text>
                  </Row>
                  {!isEmpty(conflictedKeys) && (
                    <Row noHorizontalPadding smallVerticalPadding>
                      <Text small color={publicaColors.red}>
                        {`• Please resolve highlighted conflicts before saving. Combining Preset Targeting and Custom Targeting for the same field isn't supported.`}
                      </Text>
                    </Row>
                  )}
                  {isSummaryReady && (
                    <Row smallVerticalPadding noHorizontalPadding>
                      <TargetingSummaryTable
                        columns={columns}
                        rows={rows}
                        data={data}
                        lists={lists}
                        loadState={listsLoadState}
                        conflictedKeys={conflictedKeys}
                      />
                    </Row>
                  )}
                </Rows>
              </Row>
            )}
          </Rows>
        </Row>
        {/* PRESETS */}
        <Row noPadding>
          <Rows>
            <Row noPadding>
              <Cols
                gap={spacing.small}
                style={{ alignItems: 'center', cursor: 'pointer' }}
                onClick={() => setIsPresetsOpen(!isPresetsOpen)}
              >
                <Col width={iconColWidth} displayFlex>
                  <IconV2 name='preset' small />
                </Col>
                <Col>
                  <Text small bold>
                    Presets
                  </Text>
                </Col>
                <Col>
                  <Text small bold>
                    {`(${isEmpty(presetIDs) ? 0 : presetIDs.length})`}
                  </Text>
                </Col>
                <Col displayFlex>
                  <CaretUpDown isExpanded={isPresetsOpen} />
                </Col>
              </Cols>
            </Row>
            {isPresetsOpen && (
              <Row noPadding>
                <Rows paddingLeft={`${iconColWidth + 8}px`} gap={spacing.small}>
                  <Row noPadding>
                    <Text small>
                      Tap into pre-configured sets of targeting parameters. Usually, you will need
                      to wipe existing active custom fields to let Presets operate correctly.
                    </Text>
                  </Row>
                  <Row noPadding>
                    <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                      <Col>
                        <IndependentSelect
                          placeholder={'Select Up To 5 Presets'}
                          listLabel={'Presets'}
                          options={presetOptions || []}
                          selectedOptionIDs={new Set(presetIDs)}
                          singularLabel='Preset'
                          handleApplySelect={applyPresets}
                          selectedLimit={5}
                          key='preset'
                        />
                      </Col>
                      <Col>
                        <Link
                          style={{
                            textDecoration: 'none',
                            color: 'black',
                            display: 'inline-block',
                            width: '100%',
                          }}
                          href={
                            '#' +
                            getRoutePath(
                              RouteTargetingList,
                              extractRouteParams({
                                session,
                                user,
                              }),
                            )
                          }
                          target='_blank'
                        >
                          <Div
                            extraSmallVerticalPadding
                            smallHorizontalPadding
                            style={{
                              borderRadius: borderRadius.small,
                              backgroundColor: publicaColors.veryLightGray,
                            }}
                          >
                            <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                              <Col displayFlex>
                                <IconV2 name='externalLink' small color={publicaColors.lightBlue} />
                              </Col>
                              <Col>
                                <Text small bold highlighted color={publicaColors.blue}>
                                  Go To Presets
                                </Text>
                              </Col>
                            </Cols>
                          </Div>
                        </Link>
                      </Col>
                    </Cols>
                  </Row>
                </Rows>
              </Row>
            )}
          </Rows>
        </Row>
      </Rows>
    </Panel>
  );
};
